// Shared condition evaluation utilities for action matrices

// Helper function to normalize item types for action evaluation
const normalizeItemType = (itemType) => {
  if (!itemType) return null;
  
  // Map various type representations to standard action types
  const typeMap = {
    'sell': 'buy',
    'selling': 'buy',
    'purchase': 'buy',
    'purchasing': 'buy',
    'buy': 'buy',
    'buying': 'buy',
    'sale': 'buy',
    'sales': 'buy',
    'rent': 'rent',
    'rental': 'rent',
    'renting': 'rent',
    'lending': 'rent',
    'lend':'rent'
  };
  
  return typeMap[itemType.toLowerCase()] || itemType;
};

// Helper function to evaluate conditions
export const evaluateConditions = (conditions, entity, userRole, context = {}) => {
  // Uncomment for debugging: console.log('Evaluating conditions:', { conditions, entity, userRole, entityType: context.entityType });
  
  // User role check
  if (conditions.userRole && conditions.userRole !== userRole) {
    // console.log('User role mismatch:', conditions.userRole, '!==', userRole);
    return false;
  }

  // Item type check (for order items)
  if (conditions.itemType && entity.type !== conditions.itemType) {
    const normalizedEntityType = normalizeItemType(entity.type);
    const normalizedConditionType = normalizeItemType(conditions.itemType);
    
    if (normalizedConditionType !== normalizedEntityType) {
      // console.log('Item type mismatch:', conditions.itemType, '!==', entity.type, 
      //             '(normalized:', normalizedConditionType, '!==', normalizedEntityType, ')');
      return false;
    }
  }

  // Group type check (shipping vs pickup)
  if (conditions.groupType && entity.groupType !== conditions.groupType) {
    // console.log('Group type mismatch:', conditions.groupType, '!==', entity.groupType);
    return false;
  }

  // Order type check (for fulfillment groups and orders)
  if (conditions.orderType && entity.orderType !== conditions.orderType) {
    // console.log('Order type mismatch:', conditions.orderType, '!==', entity.orderType);
    return false;
  }

  // Status checks - exact match for fulfillment group status
  if (conditions.fulfillmentGroupStatus) {
    const actualStatus = entity.fulfillmentGroup?.status || entity.status;
    if (!actualStatus || conditions.fulfillmentGroupStatus !== actualStatus) {
      // console.log('Fulfillment group status mismatch:', conditions.fulfillmentGroupStatus, '!==', actualStatus);
      return false;
    }
  }

  // Status checks - exact match for item status  
  if (conditions.itemStatus) {
    if (!entity.status || conditions.itemStatus !== entity.status) {
      // console.log('Item status mismatch:', conditions.itemStatus, '!==', entity.status);
      return false;
    }
  }

  // Status checks - for fulfillment group status on group entities
  if (conditions.status) {
    const entityStatus = entity.status;
    
    // Handle undefined/null entity status
    if (!entityStatus) {
      // console.log('Entity status is undefined/null');
      return false;
    }
    
    // Handle array of statuses (conditions.status is an array)
    if (Array.isArray(conditions.status)) {
      if (!conditions.status.includes(entityStatus)) {
        // console.log('Status array mismatch:', conditions.status, 'does not include', entityStatus);
        return false;
      }
    } else {
      // Handle single status (conditions.status is a string)
      if (conditions.status !== entityStatus) {
        // console.log('Status mismatch:', conditions.status, '!==', entityStatus);
        return false;
      }
    }
  }

  // Time constraints
  if (conditions.timeConstraints) {
    if (conditions.timeConstraints.maxDaysAfterDelivery) {
      const deliveryDate = new Date(entity.deliveredAt || entity.fulfillmentGroup?.deliveredAt);
      const daysSinceDelivery = (new Date() - deliveryDate) / (1000 * 60 * 60 * 24);
      
      if (daysSinceDelivery > conditions.timeConstraints.maxDaysAfterDelivery) {
        // console.log('Time constraint failed: days since delivery', daysSinceDelivery);
        return false;
      }
    }

    // Add 24-hour cancellation window
    if (conditions.timeConstraints.within24Hours) {
      const createdDate = new Date(entity.created_at || entity.fulfillmentGroup?.created_at);
      const hoursSinceCreated = (new Date() - createdDate) / (1000 * 60 * 60);
      
      if (hoursSinceCreated > 24) {
        // console.log('24-hour cancellation window expired:', hoursSinceCreated, 'hours');
        return false;
      }
    }
  }

  // Special conditions
  if (conditions.noActiveReturn && entity.hasActiveReturn) {
    // console.log('No active return constraint failed');
    return false;
  }

  if (conditions.noActiveGroupReturn && entity.hasActiveGroupReturn) {
    // console.log('No active group return constraint failed');
    return false;
  }

  if (conditions.isAvailableForSale && !entity.isAvailableForSale) {
    // console.log('Available for sale constraint failed');
    return false;
  }

  if (conditions.hasTracking) {
    // For fulfillment groups, check outboundDeliveries for tracking info
    const hasTrackingInfo = entity.trackingNumber || entity.trackingUrl ||
      (entity.outboundDeliveries && entity.outboundDeliveries.length > 0 && 
       entity.outboundDeliveries.some(delivery => 
         delivery.trackingNumber || delivery.tracking_number || 
         delivery.trackingUrl || delivery.tracking_url
       ));
    
    if (!hasTrackingInfo) {
      // console.log('Has tracking constraint failed - no tracking info found');
      return false;
    }
  }

  if (conditions.hasReturnTracking) {
    // For fulfillment groups, check returnDeliveries for return tracking info
    const hasReturnTrackingInfo = entity.returnTrackingNumber || 
      (entity.returnDeliveries && entity.returnDeliveries.length > 0 && 
       entity.returnDeliveries.some(delivery => delivery.trackingNumber || delivery.tracking_number));
    
    if (!hasReturnTrackingInfo) {
      // console.log('Has return tracking constraint failed - no return tracking info found');
      return false;
    }
  }

  if (conditions.minItemsForGroupReturn && entity.orderItems?.length < conditions.minItemsForGroupReturn) {
    // console.log('Min items for group return constraint failed');
    return false;
  }

  if (conditions.allItemsDelivered && !entity.orderItems?.every(item => 
    // All items must be delivered AND not canceled
    item.status === 'delivered' && !item.canceledAt
  )) {
    console.log('All items delivered constraint failed - some items not delivered or are canceled');
    return false;
  }

  if (conditions.allItemsReady && !entity.orderItems?.every(item => 
    ['ready', 'confirmed', 'pending', 'processing'].includes(item.status || '')
  )) {
    // console.log('All items ready constraint failed');
    return false;
  }

  if (conditions.rentalExtensionAvailable && !entity.canExtendRental) {
    // console.log('Rental extension available constraint failed');
    return false;
  }

  if (conditions.noExistingReview && entity.hasReview) {
    // console.log('No existing review constraint failed');
    return false;
  }

  // Order-level customer review check
  if (conditions.noExistingCustomerReview && entity.hasCustomerReview) {
    // console.log('No existing customer review constraint failed');
    return false;
  }

  if (conditions.beforeShipment) {
    const shippedStatuses = ['shipped_to_customer', 'delivery_in_progress', 'delivery_completed_to_customer'];
    const currentStatus = entity.fulfillmentGroup?.status || entity.status;
    if (currentStatus && shippedStatuses.includes(currentStatus)) {
      // console.log('Before shipment constraint failed');
      return false;
    }
  }

  // Item count conditions
  if (conditions.singleItemInGroup) {
    const itemCount = entity.orderItems?.length || entity.fulfillmentGroup?.orderItems?.length || 0;
    if (itemCount !== 1) {
      // console.log('Single item in group constraint failed - items count:', itemCount);
      return false;
    }
  }

  if (conditions.multipleItemsInGroup) {
    const itemCount = entity.orderItems?.length || entity.fulfillmentGroup?.orderItems?.length || 0;
    if (itemCount <= 1) {
      // console.log('Multiple items in group constraint failed - items count:', itemCount);
      return false;
    }
  }

  // Uncomment for debugging: console.log('All conditions passed for action');
  return true;
};

// Merchant-specific condition evaluation helper
export const evaluateMerchantConditions = (conditions, entity, userRole, context = {}) => {
  // Reuse the base evaluation from customer matrix
  const baseEvaluation = evaluateConditions(
    conditions, entity, userRole, context
  );
  
  if (!baseEvaluation) {
    return false;
  }

  // Additional merchant-specific conditions would go here if needed
  // Currently all conditions are handled in the base evaluateConditions function

  return true;
}; 